import { isDefined } from "@clipboard-health/util-ts";
import { formatTimeWithTimezone } from "@src/appV2/redesign/utils/formatTimeWithTimezone";
import {
  type GeofenceTimestampType,
  getEffectiveGeofenceArrival,
} from "@src/appV2/redesign/WorkerShifts/MissedPunches/utils/getEffectiveGeofenceArrival";
import { isAfter, isBefore, startOfMinute, subMinutes } from "date-fns";

import { checkAfterClockOutOrBreakError } from "./checkAfterClockOutOrBreakError";
import { checkMissedPunchWarning } from "./checkMissedPunchWarning";

export interface ValidateClockInTimestampParams {
  selectedTime: Date;
  // Optional reference time for "in the future" validation. Defaults to now.
  currentTime?: Date;
  geofenceArrivalTime?: Date;
  earliestPlausibleArrivalTime?: Date;
  shiftStartAt: Date;
  facilitySettings: {
    preventEarlyClockIns: boolean;
    earlyClockInMinutes?: number;
  };
  workerClockIn?: Date;
  clockOutTime?: Date;
  breakStartTime?: Date;
  isGeofenceFacility?: boolean;
  isNfcOrMeshFacility?: boolean;
  isEnforceGeofenceTimestampsEnabled?: boolean;
  verifiedClockIn?: Date;
  workplaceTimezone: string;
}

export type ClockInErrorType =
  | "GEOFENCE_ERROR"
  | "WAY_TOO_EARLY"
  | "TOO_EARLY_10_MIN"
  | "NO_EARLY_CLOCK_IN"
  | "AFTER_CLOCK_OUT_OR_BREAK"
  | "IN_FUTURE"
  | "REDUCES_VERIFIED_TIME";

export type ClockInWarningType = "MISSED_PUNCH_WARNING";

export type ClockInGeofenceValidationType = "exact" | "plausible";

export interface ClockInValidationResult {
  isValid: boolean;
  errorType?: ClockInErrorType;
  warningType?: ClockInWarningType;
  message?: string;
  geofenceValidationType?: GeofenceTimestampType;
}

const WAY_TOO_EARLY_THRESHOLD_MINUTES = 30;

export function validateClockInTimestamp(
  params: ValidateClockInTimestampParams
): ClockInValidationResult {
  const {
    selectedTime,
    currentTime,
    geofenceArrivalTime,
    earliestPlausibleArrivalTime,
    shiftStartAt,
    facilitySettings,
    workerClockIn,
    clockOutTime,
    breakStartTime,
    isGeofenceFacility = false,
    isNfcOrMeshFacility = false,
    isEnforceGeofenceTimestampsEnabled = false,
    verifiedClockIn,
    workplaceTimezone,
  } = params;

  const now = currentTime ?? new Date();

  // Priority 0: Time in the future
  const futureTimeError = checkFutureTimeError(selectedTime, workplaceTimezone, now);
  if (futureTimeError) {
    return futureTimeError;
  }

  // Priority 1: Verified time reduction error
  const verifiedTimeError = checkVerifiedTimeReductionError(selectedTime, verifiedClockIn);
  if (verifiedTimeError) {
    return verifiedTimeError;
  }

  // Priority 2: Geofence error (arrival timestamp or Earliest Plausible Arrival Timestamp)
  const geofenceError = checkGeofenceError({
    selectedTime,
    workplaceTimezone,
    geofenceArrivalTime,
    earliestPlausibleArrivalTime,
    isEnforceGeofenceTimestampsEnabled,
    verifiedClockIn,
  });
  if (geofenceError) {
    return geofenceError;
  }

  // Priority 3: Way too early (30 or more minutes before shift start)
  const wayTooEarlyError = checkWayTooEarlyError(selectedTime, shiftStartAt, verifiedClockIn);
  if (wayTooEarlyError) {
    return wayTooEarlyError;
  }

  // Priority 4: Too early when facility doesn't prevent early clock ins
  if (!facilitySettings.preventEarlyClockIns) {
    const tooEarlyError = checkTooEarlyError(
      selectedTime,
      shiftStartAt,
      workplaceTimezone,
      facilitySettings.earlyClockInMinutes
    );
    if (tooEarlyError) {
      return tooEarlyError;
    }
  }

  // Priority 5: No early clock in allowed when facility prevents early clock ins
  if (facilitySettings.preventEarlyClockIns) {
    const noEarlyClockInError = checkNoEarlyClockInError(
      selectedTime,
      shiftStartAt,
      workplaceTimezone
    );
    if (noEarlyClockInError) {
      return noEarlyClockInError;
    }
  }

  // Priority 6: After clock out or break start
  const afterClockOutOrBreakError = checkAfterClockOutOrBreakError(
    selectedTime,
    clockOutTime,
    breakStartTime
  );
  if (afterClockOutOrBreakError) {
    return afterClockOutOrBreakError;
  }

  // Check for missed punch warning (blue warning, not blocking)
  const missedPunchWarning = checkMissedPunchWarning(
    selectedTime,
    workerClockIn,
    isGeofenceFacility,
    isNfcOrMeshFacility
  );
  if (missedPunchWarning) {
    return missedPunchWarning;
  }

  // No errors or warnings - validation passed
  return {
    isValid: true,
  };
}

function checkVerifiedTimeReductionError(
  selectedTime: Date,
  verifiedClockIn: Date | undefined
): ClockInValidationResult | undefined {
  if (!isDefined(verifiedClockIn)) {
    return undefined;
  }

  // Compare at minute granularity to match what users see in the UI
  const selectedTruncated = startOfMinute(selectedTime);
  const verifiedTruncated = startOfMinute(verifiedClockIn);

  if (!isAfter(selectedTruncated, verifiedTruncated)) {
    return undefined;
  }

  return {
    isValid: false,
    errorType: "REDUCES_VERIFIED_TIME",
    message: "Clock in time cannot be set later than the verified clock in time.",
  };
}

function checkFutureTimeError(
  selectedTime: Date,
  workplaceTimezone: string,
  now: Date
): ClockInValidationResult | undefined {
  const selectedTruncated = startOfMinute(selectedTime);
  const nowTruncated = startOfMinute(now);

  if (isAfter(selectedTruncated, nowTruncated)) {
    return {
      isValid: false,
      errorType: "IN_FUTURE",
      message: `This time (${formatTimeWithTimezone(
        selectedTime,
        workplaceTimezone,
        false
      )}) is in the future. Please end your shift before requesting shift payment.`,
    };
  }

  return undefined;
}

function checkGeofenceError(params: {
  selectedTime: Date;
  workplaceTimezone: string;
  geofenceArrivalTime?: Date;
  earliestPlausibleArrivalTime?: Date;
  isEnforceGeofenceTimestampsEnabled: boolean;
  verifiedClockIn?: Date;
}): ClockInValidationResult | undefined {
  const {
    selectedTime,
    workplaceTimezone,
    geofenceArrivalTime,
    earliestPlausibleArrivalTime,
    isEnforceGeofenceTimestampsEnabled,
    verifiedClockIn,
  } = params;

  const { type: geofenceValidationType, value: geofenceTimestamp } = getEffectiveGeofenceArrival(
    geofenceArrivalTime,
    earliestPlausibleArrivalTime,
    isEnforceGeofenceTimestampsEnabled
  );

  // If verified clock in is earlier than recorded arrival, geofence is unreliable
  const isGeofenceReliable = !(
    isDefined(verifiedClockIn) &&
    isDefined(geofenceTimestamp) &&
    isBefore(verifiedClockIn, startOfMinute(geofenceTimestamp))
  );

  if (isGeofenceReliable && isDefined(geofenceTimestamp)) {
    // Compare times at minute granularity so users can safely select the same
    // displayed minute as their recorded arrival without triggering a geofence
    // error due to second-level differences in the raw timestamps.
    const selectedTruncated = startOfMinute(selectedTime);
    const arrivalTruncated = startOfMinute(geofenceTimestamp);

    if (isBefore(selectedTruncated, arrivalTruncated)) {
      return {
        isValid: false,
        errorType: "GEOFENCE_ERROR",
        message: `Clock in time cannot be set before your recorded arrival at ${formatTimeWithTimezone(
          geofenceTimestamp,
          workplaceTimezone,
          false
        )}, based on location tracking.`,
        geofenceValidationType,
      };
    }
  }

  return undefined;
}

function checkWayTooEarlyError(
  selectedTime: Date,
  shiftStartAt: Date,
  verifiedClockIn?: Date
): ClockInValidationResult | undefined {
  const wayTooEarlyThreshold = subMinutes(shiftStartAt, WAY_TOO_EARLY_THRESHOLD_MINUTES);

  // Check if selected time is before OR equal to the threshold (30+ minutes early)
  if (
    isBefore(selectedTime, wayTooEarlyThreshold) ||
    selectedTime.getTime() === wayTooEarlyThreshold.getTime()
  ) {
    const isVerifiedShift = isDefined(verifiedClockIn);

    return {
      isValid: false,
      errorType: "WAY_TOO_EARLY",
      message: isVerifiedShift
        ? "If you started working earlier than the scheduled time, please ask the workplace to reach out to Clipboard support."
        : "If you are scheduled to work earlier, please ask the workplace to update the time in the app.",
    };
  }

  return undefined;
}

function checkTooEarlyError(
  selectedTime: Date,
  shiftStartAt: Date,
  workplaceTimezone: string,
  earlyClockInMinutes?: number
): ClockInValidationResult | undefined {
  // If no early clock in minutes configured, don't apply this check
  if (!isDefined(earlyClockInMinutes) || earlyClockInMinutes === 0) {
    return undefined;
  }

  const tooEarlyThreshold = subMinutes(shiftStartAt, earlyClockInMinutes);

  if (isBefore(selectedTime, tooEarlyThreshold)) {
    return {
      isValid: false,
      errorType: "TOO_EARLY_10_MIN",
      message: `Clock in time cannot be set before ${formatTimeWithTimezone(
        tooEarlyThreshold,
        workplaceTimezone,
        false
      )} (${earlyClockInMinutes} minutes before shift start).`,
    };
  }

  return undefined;
}

function checkNoEarlyClockInError(
  selectedTime: Date,
  shiftStartAt: Date,
  workplaceTimezone: string
): ClockInValidationResult | undefined {
  if (isBefore(selectedTime, shiftStartAt)) {
    return {
      isValid: false,
      errorType: "NO_EARLY_CLOCK_IN",
      message: `Clock in time cannot be set before ${formatTimeWithTimezone(
        shiftStartAt,
        workplaceTimezone,
        false
      )} (shift start).`,
    };
  }

  return undefined;
}
